/**
 * React bridge for WebView ↔ native app messaging.
 * Types live in `./index.ts` (`AppMessage`, `WebMessage`).
 */

import type React from "react";
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    AppMessage,
    isEmbeddedNativeWebView,
    type ReactNativeAppState,
    type WebMessage,
} from "./index";

type AppMessageListener<T> = (payload: T) => void;

type AppMessageMap = {
    [K in AppMessage["type"]]: Extract<AppMessage, { type: K }>["payload"];
};

type WebMessageMap = {
    [K in WebMessage["type"]]: Extract<WebMessage, { type: K }>["payload"];
};

export type AppMessageWebOverrides = Partial<{
    [K in WebMessage["type"]]: (payload: WebMessageMap[K] | undefined) => void;
}>;

type AppMessageManager = {
    subscribe<K extends keyof AppMessageMap>(
        message: K,
        listener: AppMessageListener<AppMessageMap[K]>,
    ): () => void;
    sendAppMessage<K extends keyof WebMessageMap>(
        message: K,
        payload?: WebMessageMap[K],
    ): void;
    isEmbeddedAppContext: boolean;
    /** Latest native app state from `reactNativeAppStateInitialized`, merged with permission responses. */
    appState: ReactNativeAppState | null;
};

const AppMessageContext = createContext<AppMessageManager | null>(null);

export type AppMessageProviderProps = {
    children: React.ReactNode;
    /** When set, `sendAppMessage` calls these first and skips the native bridge if the handler runs. */
    overrides?: AppMessageWebOverrides;
    /**
     * When false, this provider does not register `window` postMessage listeners.
     * Use for a nested provider so only the outermost one receives native messages.
     * @default true
     */
    subscribeToNativeBridge?: boolean;
};

export async function fetchAccessTokenFromNativeiOSClient(): Promise<string | null> {
    if (!window.isIosNativeWebView || !window.isNewAuthEnabled) {
        return null;
    }

    if (!window.webkit?.messageHandlers?.iosNativeWebView?.postMessage) {
        console.error("iOS WebView message handler is not available");
        return null;
    }
    const message: WebMessage = {
        type: "fetchAuthAccessToken",
        payload: undefined,
    };
    const json = JSON.stringify(message);
    const reply =
        await window.webkit.messageHandlers.iosNativeWebViewWithReply.postMessage(json);

    return typeof reply === "string" ? reply : null;
}

export function postWebMessageToNative(message: WebMessage) {
    const json = JSON.stringify(message);

    if (window.isReactNativeWebView) {
        if (!window.ReactNativeWebView?.postMessage) {
            console.error("postMessage is not available on React Native WebView");
            return;
        }
        window.ReactNativeWebView.postMessage(json);
    }

    if (window.isIosNativeWebView) {
        if (!window.webkit?.messageHandlers?.iosNativeWebView?.postMessage) {
            console.error("iOS WebView message handler is not available");
            return;
        }
        window.webkit.messageHandlers.iosNativeWebView.postMessage(json);
    }

    if (window.AndroidBridge?.postMessage) {
        window.AndroidBridge.postMessage(json);
    }
}

function ensureBridgeReplyRegistry() {
    if (typeof window === "undefined") {
        return;
    }
    window.albertineBridgeReplies ??= {};
    window.albertineResolveBridgeReply ??= (replyId, value) => {
        const resolve = window.albertineBridgeReplies?.[replyId];
        if (!resolve) {
            return;
        }
        delete window.albertineBridgeReplies![replyId];
        resolve(value);
    };
}

const NATIVE_BRIDGE_REPLY_TIMEOUT_MS = 10_000;

async function postNativeMessageWithReply(json: string): Promise<unknown> {
    if (
        window.isIosNativeWebView &&
        window.webkit?.messageHandlers?.iosNativeWebViewWithReply
    ) {
        return window.webkit.messageHandlers.iosNativeWebViewWithReply.postMessage(
            json,
        );
    }

    if (window.AndroidBridge?.postMessageWithReply) {
        ensureBridgeReplyRegistry();
        const replyId = crypto.randomUUID();
        return new Promise((resolve) => {
            const timeoutId = window.setTimeout(() => {
                delete window.albertineBridgeReplies![replyId];
                resolve(null);
            }, NATIVE_BRIDGE_REPLY_TIMEOUT_MS);
            window.albertineBridgeReplies![replyId] = (value) => {
                window.clearTimeout(timeoutId);
                resolve(value);
            };
            window.AndroidBridge!.postMessageWithReply!(json, replyId);
        });
    }

    return null;
}

export async function fetchAccessTokenFromNativeClient(): Promise<string | null> {
    const json = JSON.stringify({
        type: "fetchAuthAccessToken",
        payload: undefined,
    } satisfies WebMessage);
    const reply = await postNativeMessageWithReply(json);
    return typeof reply === "string" && reply.length > 0 ? reply : null;
}

export function AppMessageProvider({
    children,
    overrides,
    subscribeToNativeBridge = true,
}: AppMessageProviderProps) {
    const [appState, setAppState] = useState<ReactNativeAppState | null>(null);

    const listenersRef = useRef<{
        [K in keyof AppMessageMap]?: Set<AppMessageListener<unknown>>;
    }>({});

    const overridesRef = useRef(overrides);
    overridesRef.current = overrides;

    const subscribeToAppMessage = useCallback(
        <K extends keyof AppMessageMap>(
            event: K,
            listener: AppMessageListener<AppMessageMap[K]>,
        ) => {
            if (!listenersRef.current[event]) {
                listenersRef.current[event] = new Set();
            }
            listenersRef.current[event]?.add(listener as AppMessageListener<unknown>);
            return () => {
                listenersRef.current[event]?.delete(
                    listener as AppMessageListener<unknown>,
                );
            };
        },
        [],
    );

    const emitAppMessageToListeners = useCallback(
        <K extends keyof AppMessageMap>(message: K, payload: AppMessageMap[K]) => {
            listenersRef.current[message]?.forEach((listener) => listener(payload));
        },
        [],
    );

    const receiveMessageFromWebViewPostMessage = useCallback(
        (message: MessageEvent) => {
            console.log("Received message from React Native WebView:", message.data);
            const raw = (() => {
                try {
                    return JSON.parse(message.data as string) as unknown;
                } catch (error) {
                    console.log("Failed to parse message data:", error, message);
                    return;
                }
            })();

            if (!raw || typeof raw !== "object") {
                console.log("Received invalid data from React Native WebView:", raw);
                return;
            }

            const parsed = AppMessage.safeParse(raw);
            if (parsed.success) {
                emitAppMessageToListeners(parsed.data.type, parsed.data.payload);
            } else {
                console.log("Invalid AppMessage format", raw);
            }
        },
        [emitAppMessageToListeners],
    );

    useEffect(() => {
        const unsubscribeInit = subscribeToAppMessage(
            "reactNativeAppStateInitialized",
            (payload) => {
                setAppState(payload);
            },
        );
        const unsubscribePush = subscribeToAppMessage(
            "pushNotificationsPermissionsResponse",
            (payload) => {
                setAppState((prev) => {
                    if (!prev) {
                        return prev;
                    }
                    if (payload.pushNotificationsState === "authorized") {
                        return {
                            ...prev,
                            pushNotifications: {
                                state: "authorized",
                                token: payload.pushNotificationsToken,
                            },
                        };
                    }
                    if (payload.pushNotificationsState === "notDetermined") {
                        return {
                            ...prev,
                            pushNotifications: {
                                state: "notDetermined",
                                canAskAgain: true,
                            },
                        };
                    }
                    const previous = prev.pushNotifications;
                    const canAskAgain =
                        previous.state === "denied" ? previous.canAskAgain : true;
                    return {
                        ...prev,
                        pushNotifications: {
                            state: "denied",
                            canAskAgain,
                        },
                    };
                });
            },
        );
        return () => {
            unsubscribeInit();
            unsubscribePush();
        };
    }, [subscribeToAppMessage]);

    useEffect(() => {
        if (!subscribeToNativeBridge) {
            return;
        }

        if (!isEmbeddedNativeWebView()) {
            return;
        }

        window.addEventListener("message", receiveMessageFromWebViewPostMessage, true);

        return () => {
            window.removeEventListener(
                "message",
                receiveMessageFromWebViewPostMessage,
                true,
            );
        };
    }, [receiveMessageFromWebViewPostMessage, subscribeToNativeBridge]);

    const sendAppMessage = useCallback(
        <K extends keyof WebMessageMap>(event: K, payload?: WebMessageMap[K]) => {
            const override = overridesRef.current?.[event] as
                | ((p?: WebMessageMap[K]) => void)
                | undefined;
            if (override) {
                override(payload);
                return;
            }

            postWebMessageToNative({
                type: event,
                payload,
            } as WebMessage);
        },
        [],
    );

    const embedded = isEmbeddedNativeWebView();

    const manager = useMemo(
        (): AppMessageManager => ({
            subscribe: subscribeToAppMessage,
            sendAppMessage,
            isEmbeddedAppContext: embedded,
            appState,
        }),
        [appState, embedded, sendAppMessage, subscribeToAppMessage],
    );

    return (
        <AppMessageContext.Provider value={manager}>
            {children}
        </AppMessageContext.Provider>
    );
}

export function useAppMessageContext() {
    const context = useContext(AppMessageContext);
    if (!context) {
        throw new Error("useAppMessageContext must be used within AppMessageProvider");
    }
    return context;
}

export function useIsEmbeddedAppContext() {
    const context = useContext(AppMessageContext);
    if (!context) {
        throw new Error(
            "useIsEmbeddedAppContext must be used within AppMessageProvider",
        );
    }
    return context.isEmbeddedAppContext;
}

export function useAppMessageSender() {
    const context = useContext(AppMessageContext);
    if (!context) {
        throw new Error("useAppMessageSender must be used within AppMessageProvider");
    }
    return context.sendAppMessage;
}

export function useAppState(): ReactNativeAppState | null {
    const context = useContext(AppMessageContext);
    if (!context) throw new Error("useAppState must be used within AppMessageProvider");
    return context.appState;
}

export function useAppMessageListener<K extends keyof AppMessageMap>(
    event: K,
    listener: AppMessageListener<AppMessageMap[K]>,
) {
    const { subscribe } = useAppMessageContext();

    useEffect(() => {
        const unsubscribe = subscribe(event, listener);
        return unsubscribe;
    }, [event, listener, subscribe]);
}

export default AppMessageProvider;
